import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, MapPin, Truck, MessageCircle } from "lucide-react";
import Hero from "@/components/Hero";
import EditorialMarquee from "@/components/Marquee";
import Manifesto from "@/components/Manifesto";
import ProductCard from "@/components/ProductCard";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { fetchProducts } from "@/lib/api";
import { STORE, IMAGES } from "@/lib/catalog";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetchProducts()
      .then((data) => setFeatured(data.filter((p) => p.featured).slice(0, 4)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />
      <EditorialMarquee />

      <section className="bg-bone py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>This Week's Essentials</Eyebrow>
              <h2 className="mt-4 font-serif font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink">
                Stocked fresh, <span className="italic text-forest">priced honest.</span>
              </h2>
            </div>
            <Link
              to="/products"
              className="group inline-flex items-center gap-2 text-sm font-bold text-forest hover:text-marigold transition-colors"
              data-testid="home-view-all-products-link"
            >
              View full catalog
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-forest-dark text-bone overflow-hidden">
        <div className="grain pointer-events-none absolute inset-0 opacity-[0.06]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow className="text-saffron">Visit the Store</Eyebrow>
            <h2 className="mt-4 font-serif font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">
              Right around the corner,
              <span className="italic text-saffron"> open every single day.</span>
            </h2>
            <p className="mt-5 text-bone/70 leading-relaxed max-w-lg">
              Walk in for your monthly ration, a packet of biscuits, or just a chat.
              We have been serving Civil Lines and nearby neighbourhoods for over two decades.
            </p>
            <div className="mt-8 space-y-4 text-sm">
              <p className="flex items-center gap-3 text-bone/85">
                <Clock className="w-4 h-4 text-saffron shrink-0" /> {STORE.hours}
              </p>
              <p className="flex items-center gap-3 text-bone/85">
                <MapPin className="w-4 h-4 text-saffron shrink-0" /> {STORE.address}
              </p>
              <p className="flex items-center gap-3 text-bone/85">
                <Truck className="w-4 h-4 text-saffron shrink-0" /> Free same-day delivery in the neighbourhood
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-3xl border-4 border-bone/10 shadow-2xl">
              <img
                src={IMAGES.market}
                alt="Fresh produce market stall"
                className="h-72 lg:h-96 w-full object-cover hover:scale-105 transition-transform duration-700"
                data-testid="home-store-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <Manifesto />

      <section className="bg-cream border-t border-line py-20 lg:py-24" data-testid="home-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink">
              Skip the queue. <span className="italic text-marigold">Send us your list.</span>
            </h2>
            <p className="mt-4 text-ink-soft max-w-xl mx-auto">
              WhatsApp your shopping list or send an enquiry online — we will pack it fresh
              and deliver it to your door, or keep it ready for pickup.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a
                href={STORE.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-bone hover:bg-forest-deep transition-colors"
                data-testid="home-whatsapp-button"
              >
                <MessageCircle className="w-4 h-4" /> Order on WhatsApp
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border-2 border-forest/25 px-7 py-3.5 text-sm font-semibold text-forest hover:bg-forest hover:text-bone transition-colors"
                data-testid="home-enquiry-button"
              >
                Send an Enquiry
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
